// T2T-05-C5 — Full /notifications page. Date-grouped list (Today / Yesterday /
// This Week / Older), filter chips by event_type and read state, click to
// mark-read + navigate to action_url.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import {
  fetchNotificationFeed,
  markAllRead,
  markOneRead,
  deleteNotification,
} from "../../../api/t2tNotificationsApi";
import useBellSse from "./useBellSse";

const PAGE_SIZE = 50;

const FILTER_CHIPS = [
  { key: "all",          label: "All",          where: () => true },
  { key: "unread",       label: "Unread",       where: (n) => !n.is_read },
  { key: "chat_mention", label: "@Mentions",    where: (n) => n.event_type === "chat_mention" },
  { key: "chat_message_new", label: "Chat",     where: (n) => n.event_type === "chat_message_new" || n.event_type === "chat_vendor_replied" },
  { key: "request",      label: "Request",      where: (n) => n.event_type?.startsWith("request_") || n.event_type?.startsWith("vendor_") },
  { key: "sla",          label: "SLA",          where: (n) => n.event_type?.startsWith("sla_") },
];

function bucketFor(d) {
  if (!d) return "older";
  const created = new Date(d);
  const now = new Date();
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfToday.getDate() - 1);
  const startOfWeek = new Date(startOfToday); startOfWeek.setDate(startOfToday.getDate() - 7);
  if (created >= startOfToday)     return "today";
  if (created >= startOfYesterday) return "yesterday";
  if (created >= startOfWeek)      return "this-week";
  return "older";
}
const BUCKET_LABEL = { today: "Today", yesterday: "Yesterday", "this-week": "This Week", older: "Older" };
const BUCKET_ORDER = ["today", "yesterday", "this-week", "older"];

const ICON_FOR_EVENT = (et) => {
  if (!et) return "bi-bell";
  if (et === "chat_mention")              return "bi-at";
  if (et.startsWith("chat_"))             return "bi-chat-dots";
  if (et.startsWith("sla_"))              return "bi-clock-history";
  if (et === "request_assigned" || et === "request_reassigned") return "bi-person-check";
  if (et.startsWith("request_") || et.startsWith("vendor_"))    return "bi-file-earmark-text";
  return "bi-bell";
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset]   = useState(0);
  const [filter, setFilter]   = useState("all");
  // T2T-05-C7: cap rendered rows past 200 to keep the DOM responsive.
  const [showAll, setShowAll] = useState(false);
  const RENDER_CAP = 200;

  const load = useCallback(async (resetOffset = false) => {
    setLoading(true);
    const o = resetOffset ? 0 : offset;
    const resp = await fetchNotificationFeed({ limit: PAGE_SIZE, offset: o });
    if (!resp.error) {
      const payload = resp.data?.data || {};
      const incoming = payload.rows || [];
      setRows((prev) => resetOffset ? incoming : [...prev, ...incoming]);
      setHasMore(!!payload.has_more);
      setOffset(o + incoming.length);
    }
    setLoading(false);
  }, [offset]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, []);

  // Live updates via SSE — prepend any new arrivals
  useBellSse({
    onNotificationNew: (row) => setRows((prev) => prev[0]?.id === row?.id ? prev : [row, ...prev]),
  });

  const visible = useMemo(() => {
    const f = FILTER_CHIPS.find(c => c.key === filter) || FILTER_CHIPS[0];
    return rows.filter(f.where);
  }, [rows, filter]);

  // T2T-05-C7: simple windowing — slice visible rows past RENDER_CAP unless
  // the user explicitly opts into the full list. Older rows still get fetched
  // on Load older but stop rendering past the cap.
  const renderedRows = useMemo(() => {
    if (showAll || visible.length <= RENDER_CAP) return visible;
    return visible.slice(0, RENDER_CAP);
  }, [visible, showAll]);
  const truncated = visible.length - renderedRows.length;

  const grouped = useMemo(() => {
    const out = { today: [], yesterday: [], "this-week": [], older: [] };
    for (const n of renderedRows) out[bucketFor(n.created_at)].push(n);
    return out;
  }, [renderedRows]);

  const handleClick = async (n) => {
    if (!n.is_read) {
      await markOneRead(n.id).catch(() => {});
      setRows((prev) => prev.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
    }
    if (n.action_url) navigate(n.action_url);
  };

  const handleDelete = async (e, n) => {
    e.stopPropagation();
    await deleteNotification(n.id).catch(() => {});
    setRows((prev) => prev.filter(x => x.id !== n.id));
  };

  const handleMarkAllRead = async () => {
    await markAllRead().catch(() => {});
    setRows((prev) => prev.map(x => ({ ...x, is_read: 1 })));
  };

  return (
    <div className="t2t-module" style={{ minHeight: "100vh", background: "var(--t2t-bg)" }}>
      <T2TTopNav title="Notifications" actions={
        <button
          className="t2t-btn t2t-btn-ghost t2t-btn-sm"
          onClick={() => navigate("/t2t/notifications/preferences")}
          title="Notification preferences"
        >
          <i className="bi bi-gear" /> Preferences
        </button>
      } />

      {/* Filter chips */}
      <div style={{ padding: "12px 18px", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", background: "var(--t2t-surface)", borderBottom: "1px solid var(--t2t-border)" }}>
        <div className="t2t-tab-group" style={{ background: "none", padding: 0, gap: 4 }}>
          {FILTER_CHIPS.map(c => (
            <button
              key={c.key}
              className={`t2t-tab${filter === c.key ? " active" : ""}`}
              onClick={() => setFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={handleMarkAllRead}>
          <i className="bi bi-check2-all" /> Mark all read
        </button>
      </div>

      {/* List */}
      <div style={{ background: "var(--t2t-bg)", paddingBottom: 40 }}>
        {loading && rows.length === 0 ? (
          <div style={{ padding: 24 }}>
            {[80, 60, 90, 70, 75].map((w, i) => (
              <div key={i} className="kb-skeleton-bar" style={{ width: `${w}%`, height: 14, marginBottom: 14, borderRadius: 6 }} />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 80, textAlign: "center" }}>
            <i className="bi bi-bell" style={{ fontSize: 40, color: "var(--t2t-border)", marginBottom: 12 }} />
            <p style={{ fontWeight: 600, fontSize: 14, color: "var(--t2t-text-secondary)" }}>No notifications</p>
            <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", maxWidth: 320, margin: "0 auto" }}>
              {filter === "all"
                ? "When something needs your attention, it'll show up here."
                : "Nothing matches the current filter."}
            </p>
          </div>
        ) : (
          BUCKET_ORDER.map(b => {
            if (grouped[b].length === 0) return null;
            return (
              <div key={b}>
                <div className="t2t-notif-section-header">{BUCKET_LABEL[b]}</div>
                {grouped[b].map(n => (
                  <div
                    key={n.id}
                    className={`t2t-notif-row${n.is_read ? "" : " unread"}`}
                    onClick={() => handleClick(n)}
                  >
                    <div className="t2t-notif-row-icon">
                      <i className={`bi ${ICON_FOR_EVENT(n.event_type)}`} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: "var(--t2t-text-primary)", marginBottom: 2 }}>
                        {n.title}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)", lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {n.body}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 4 }}>
                        {new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <button
                      className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                      onClick={(e) => handleDelete(e, n)}
                      title="Delete"
                      style={{ alignSelf: "flex-start", color: "var(--t2t-text-muted)" }}
                    >
                      <i className="bi bi-x" />
                    </button>
                  </div>
                ))}
              </div>
            );
          })
        )}

        {truncated > 0 && (
          <div style={{
            textAlign: "center", padding: "10px 14px", fontSize: 11,
            color: "var(--t2t-text-muted)", background: "var(--t2t-surface-muted, #F8FAFC)",
          }}>
            Showing {renderedRows.length.toLocaleString()} of {visible.length.toLocaleString()} —{" "}
            {!showAll
              ? <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setShowAll(true)}>show all</button>
              : <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setShowAll(false)}>collapse</button>}
            {" · "}or use a filter to narrow down.
          </div>
        )}

        {hasMore && !loading && (
          <div style={{ textAlign: "center", padding: 14 }}>
            <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => load(false)}>
              Load older
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
